import {
  ClientToServerMessage,
  MultiplayerStateManagerError,
  ReconnectingWebSocket,
  ReconnectingWebSocketState,
  ServerToClientMessage,
  UserActivityDetector,
} from "@noya-app/state-manager";

export type ConnectionEvent<State> =
  | {
      type: "stateChange";
      state: ReconnectingWebSocketState;
    }
  | {
      type: "send";
      message: ClientToServerMessage<State>;
    }
  | {
      type: "receive";
      message: ServerToClientMessage<State>;
    }
  | {
      type: "error";
      error: MultiplayerStateManagerError;
    };

export type ConnectionOptions<State> = {
  debug?: boolean;
  onConnectionEvent?: (event: ConnectionEvent<State>) => void;
};

export class WebSocketConnection<State> {
  private ws: ReconnectingWebSocket;
  private closedForever = false;

  constructor(
    private url: URL,
    private options: ConnectionOptions<State> = {}
  ) {
    this.ws = new ReconnectingWebSocket({
      debug: options.debug,
      onopen: this.handleOpen,
      onmessage: this.handleMessage,
      onclose: this.handleClose,
      activityDetector: new UserActivityDetector(),
    });
  }

  async connect() {
    if (this.closedForever) return;

    this.ws.connect(this.url.toString());
  }

  private handleOpen = () => {
    if (this.closedForever) return;

    if (this.options.debug) {
      console.info("ws connected");
    }

    this.options.onConnectionEvent?.({
      type: "stateChange",
      state: "OPEN",
    });
  };

  private handleMessage = (event: MessageEvent) => {
    if (this.closedForever) return;

    if (this.options.debug) {
      console.info("ws receiving message ", event.data);
    }

    const parsed = JSON.parse(event.data) as ServerToClientMessage<State>;

    this.options.onConnectionEvent?.({
      type: "receive",
      message: parsed,
    });
  };

  private handleClose = () => {
    if (this.closedForever) return;

    if (this.options.debug) {
      console.info("ws disconnected");
    }

    this.options.onConnectionEvent?.({
      type: "stateChange",
      state: "CLOSED",
    });
  };

  send(message: ClientToServerMessage<State>) {
    if (this.closedForever) return;

    if (this.options.debug) {
      console.info("ws sending message", message);
    }

    this.options.onConnectionEvent?.({
      type: "send",
      message,
    });

    this.ws.send(JSON.stringify(message));
  }

  close() {
    if (this.closedForever) return;

    this.closedForever = true;
    this.ws.shutdown();

    // remove all listeners
    this.ws.listeners.forEach((listener) => {
      this.ws.removeListener(listener);
    });
  }

  addListener(listener: (state: ReconnectingWebSocketState) => void) {
    return this.ws.addListener(listener);
  }

  removeListener(listener: (state: ReconnectingWebSocketState) => void) {
    return this.ws.removeListener(listener);
  }
}
